import { ArrowLeft, Database, LoaderCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import SemanticObjectKnowledge from '@/frontend/features/Fde/data/SemanticObjectKnowledge'
import SemanticObjectOverview from '@/frontend/features/Fde/data/SemanticObjectOverview'
import SemanticObjectRelationships from '@/frontend/features/Fde/data/SemanticObjectRelationships'
import useSemanticObjectView from '@/frontend/features/Fde/useSemanticObjectView'

interface Props {
  projectId: string
  classId: string
  objectId: string
  copy: Record<string, string>
  onBack: () => void
  onOpenObject: (classId: string, objectId: string) => void
}

export default function SemanticObject360(props: Props) {
  const { result, error, loading } = useSemanticObjectView({
    projectId: props.projectId,
    classId: props.classId,
    objectId: props.objectId,
    errorLabel: props.copy.semantic_object_view_error,
  })

  if (loading) return <div className="flex h-full items-center justify-center gap-2 text-xs text-muted-foreground"><LoaderCircle className="size-4 animate-spin" />{props.copy.semantic_object_view_loading}</div>
  if (error || !result) return <div className="mx-auto max-w-3xl space-y-4"><BackButton label={props.copy.semantic_object_view_back} onClick={props.onBack} /><p className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">{error || props.copy.semantic_object_view_error}</p></div>

  return <div className="mx-auto max-w-6xl space-y-4">
    <BackButton label={props.copy.semantic_object_view_back} onClick={props.onBack} />
    <header className="flex items-start gap-3 border-b border-border pb-4">
      <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Database className="size-4" /></span>
      <div className="min-w-0 flex-1"><small className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{result.object_type.name}</small><h2 className="truncate text-xl font-semibold">{result.object.title}</h2><p className="mt-1 truncate text-[10px] text-muted-foreground">{props.copy.semantic_values_from_forge} · {(result.sources || [result.source]).map((source) => source.name).join(', ')}</p></div>
      <Badge variant={result.coverage.available === result.coverage.declared ? 'success' : 'warning'}>{result.coverage.available}/{result.coverage.declared} {props.copy.semantic_coverage_short}</Badge>
    </header>
    {result.attention.length > 0 && <div className="flex flex-wrap gap-2">{result.attention.map((item) => <span key={item.kind} title={item.detail} className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-medium text-amber-900">{item.label}</span>)}</div>}
    <Tabs defaultValue="overview" className="gap-0">
      <TabsList className="h-10 bg-muted/50 p-1"><TabsTrigger value="overview">{props.copy.semantic_360_overview}</TabsTrigger><TabsTrigger value="relationships">{props.copy.semantic_360_relationships} <Count value={result.relationships.length} /></TabsTrigger><TabsTrigger value="knowledge">{props.copy.semantic_360_knowledge}</TabsTrigger></TabsList>
      <TabsContent value="overview" className="mt-4"><SemanticObjectOverview result={result} copy={props.copy} /></TabsContent>
      <TabsContent value="relationships" className="mt-4"><SemanticObjectRelationships relationships={result.relationships} copy={props.copy} onOpenObject={props.onOpenObject} /></TabsContent>
      <TabsContent value="knowledge" className="mt-4"><SemanticObjectKnowledge projectId={props.projectId} query={result.view_contract.knowledge.query} verification={result.view_contract.knowledge.verification} copy={props.copy} /></TabsContent>
    </Tabs>
  </div>
}

function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" onClick={onClick} className="inline-flex items-center gap-1 text-xs font-medium text-primary"><ArrowLeft className="size-3.5" />{label}</button>
}

function Count({ value }: { value: number }) {
  return <span className="ml-1 rounded-full bg-background/80 px-1.5 text-[9px] tabular-nums">{value}</span>
}
